import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FloppyDisk,
  FilePdf,
  ArrowLeft,
  Spinner,
  Plus,
  Trash,
  Buildings,
  Car,
  MapTrifold,
  HandWaving,
} from "@phosphor-icons/react";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "AUD", "SGD", "THB", "MYR", "JPY", "CHF", "IDR", "OMR", "MAD"];

const SERVICE_TYPES = [
  { key: "hotel", label: "Hotel", icon: Buildings },
  { key: "transfer", label: "Transfer", icon: Car },
  { key: "tour", label: "Tour", icon: MapTrifold },
  { key: "meet_and_greet", label: "Meet & Greet", icon: HandWaving },
];

const TRANSFER_TYPES = ["Airport Pickup", "Airport Drop", "Intercity Transfer", "Point-to-Point", "Round Trip"];
const VEHICLE_TYPES = ["Sedan", "SUV", "Van", "Minibus", "Bus", "Luxury Sedan", "Limousine"];
const TOUR_TYPES = ["City Tour", "Day Trip", "Excursion", "Adventure", "Cultural", "Shopping", "Food & Wine"];
const GUIDE_LANGUAGES = ["English", "Spanish", "French", "German", "Italian", "Japanese", "Mandarin", "Arabic", "Hindi", "Russian"];

const DEFAULT_INCLUSIONS = {
  hotel: ["Breakfast", "Airport Transfer", "Late Checkout", "Room Upgrade (subject to availability)", "Wi-Fi", "Gym & Pool Access"],
  transfer: ["Meet at arrival hall", "Luggage assistance", "Child seat (on request)", "Water bottles", "Toll charges included", "Waiting time: 60 min"],
  tour: ["English-speaking guide", "Entrance fees", "Lunch", "Hotel pickup & drop", "Air-conditioned transport", "Water & snacks"],
  meet_and_greet: ["Greeter at arrival gate", "Signboard with guest name", "Luggage assistance", "Fast-track immigration (if available)", "Transfer to hotel", "Welcome drink"],
};

const ConfirmationEditorPage = () => {
  const navigate = useNavigate();
  const { id: confId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(confId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!confId);

  const [form, setForm] = useState({
    service_type: "hotel",
    guest_name: "",
    hotel_name: "",
    hotel_address: "",
    confirmation_ref: "",
    confirmation_date: new Date().toISOString().split("T")[0],
    check_in: "",
    check_out: "",
    nights: 0,
    num_guests: 0,
    room_type: "",
    num_rooms: 1,
    rooms: [{ room_type: "", num_rooms: 1, rate_per_night: 0 }],
    meal_plan: "",
    show_pricing: false,
    rate_per_night: 0,
    total_cost: 0,
    currency: "USD",
    payment_status: "",
    cancellation_policy: "",
    special_requests: "",
    inclusions: [],
    notes: "",
    consultant_name: "",
    consultant_designation: "",
    consultant_phone: "",
    consultant_email: "",
    // Transfer
    pickup_location: "",
    pickup_time: "",
    pickup_date: "",
    dropoff_location: "",
    vehicle_type: "",
    flight_number: "",
    num_passengers: 0,
    driver_contact: "",
    transfer_type: "",
    // Tour
    tour_name: "",
    tour_date: "",
    tour_duration: "",
    guide_language: "",
    tour_type: "",
    // Meet & Greet
    arrival_time: "",
    greeter_name: "",
    signboard_name: "",
    transfer_to: "",
    airport_terminal: "",
  });

  useEffect(() => {
    if (isEditing) {
      Promise.all([
        api.get(`/confirmations/${confId}`),
        api.get("/settings"),
      ]).then(([confRes, settingsRes]) => {
        const c = confRes.data;
        const s = settingsRes.data;
        const rooms = c.rooms?.length
          ? c.rooms
          : c.room_type
            ? [{ room_type: c.room_type, num_rooms: c.num_rooms || 1, rate_per_night: c.rate_per_night || 0 }]
            : [{ room_type: "", num_rooms: 1, rate_per_night: 0 }];
        setForm({
          ...c,
          service_type: c.service_type || "hotel",
          rooms,
          consultant_name: c.consultant_name || s.consultant_name || "",
          consultant_designation: c.consultant_designation || s.consultant_designation || "",
          consultant_phone: c.consultant_phone || s.consultant_phone || "",
          consultant_email: c.consultant_email || s.consultant_email || "",
        });
      }).catch(() => { toast.error("Not found"); navigate("/confirmations"); })
        .finally(() => setLoading(false));
    } else {
      api.get("/settings").then((res) => {
        const s = res.data;
        setForm((f) => ({
          ...f,
          guest_name: searchParams.get("guest") || f.guest_name,
          hotel_name: searchParams.get("hotel") || f.hotel_name,
          hotel_address: searchParams.get("address") || f.hotel_address,
          check_in: searchParams.get("checkin") || f.check_in,
          check_out: searchParams.get("checkout") || f.check_out,
          nights: parseInt(searchParams.get("nights")) || f.nights,
          room_type: searchParams.get("room") || f.room_type,
          currency: searchParams.get("currency") || f.currency,
          consultant_name: s.consultant_name || "",
          consultant_designation: s.consultant_designation || "",
          consultant_phone: s.consultant_phone || "",
          consultant_email: s.consultant_email || "",
        }));
      }).catch(() => {});
    }
  }, [confId, isEditing, navigate, searchParams]);

  const update = (field, value) => {
    setForm((f) => {
      const updated = { ...f, [field]: value };
      if (field === "check_in" || field === "check_out") {
        if (updated.check_in && updated.check_out) {
          const [y1, m1, d1] = updated.check_in.split("-").map(Number);
          const [y2, m2, d2] = updated.check_out.split("-").map(Number);
          const nights = Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
          if (nights > 0) updated.nights = nights;
        }
      }
      return updated;
    });
  };

  const toggleInclusion = (inc) => {
    setForm((f) => ({
      ...f,
      inclusions: f.inclusions.includes(inc) ? f.inclusions.filter((i) => i !== inc) : [...f.inclusions, inc],
    }));
  };

  const updateRoom = (idx, field, value) => {
    setForm((f) => ({ ...f, rooms: f.rooms.map((r, i) => (i === idx ? { ...r, [field]: value } : r)) }));
  };
  const addRoom = () => setForm((f) => ({ ...f, rooms: [...f.rooms, { room_type: "", num_rooms: 1, rate_per_night: 0 }] }));
  const removeRoom = (idx) => setForm((f) => ({ ...f, rooms: f.rooms.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.guest_name && !form.hotel_name && !form.tour_name && !form.pickup_location) {
      toast.error("Please fill in basic details");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/confirmations/${confId}`, form);
        toast.success("Updated");
      } else {
        const res = await api.post("/confirmations", form);
        toast.success("Saved");
        navigate(`/confirmations/${res.data.id}/edit`, { replace: true });
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const downloadPdf = async () => {
    if (!confId) { toast.error("Please save first"); return; }
    setGeneratingPdf(true);
    try {
      const res = await api.get(`/confirmations/${confId}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(res.data);
      a.download = `Confirmation_${form.guest_name || "letter"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const sType = form.service_type || "hotel";
  const availableInclusions = DEFAULT_INCLUSIONS[sType] || DEFAULT_INCLUSIONS.hotel;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl" data-testid="confirmation-editor">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/confirmations")} data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-1">
            {isEditing ? "Edit" : "New"} Confirmation
          </p>
          <h1 className="font-serif text-xl sm:text-2xl text-navy">
            {SERVICE_TYPES.find((s) => s.key === sType)?.label || "Hotel"} Confirmation Letter
          </h1>
        </div>
        <Button variant="outline" onClick={downloadPdf} disabled={!confId || generatingPdf} className="gap-2" data-testid="download-pdf-btn">
          {generatingPdf ? <Spinner size={16} className="animate-spin" /> : <FilePdf size={16} />}
          <span className="hidden sm:inline">PDF</span>
        </Button>
        <Button onClick={save} disabled={saving} className="bg-navy hover:bg-[#2a3a5e] text-white gap-2" data-testid="save-confirmation-btn">
          <FloppyDisk size={16} /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Service Type Selector */}
      <div className="bg-white border border-border p-4 mb-4">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Service Type</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SERVICE_TYPES.map((st) => (
            <button
              key={st.key}
              onClick={() => update("service_type", st.key)}
              className={`flex items-center gap-2 p-3 border text-sm transition-colors ${
                sType === st.key
                  ? "border-gold bg-gold/5 text-navy font-medium"
                  : "border-border bg-white text-muted-foreground hover:border-gold/50 hover:bg-cream/30"
              }`}
              data-testid={`service-type-${st.key}`}
            >
              <st.icon size={18} weight={sType === st.key ? "fill" : "regular"} />
              {st.label}
            </button>
          ))}
        </div>
      </div>

      {/* Common Fields */}
      <div className="bg-white border border-border p-5 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Guest & Reference</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Guest Name</Label>
            <Input value={form.guest_name} onChange={(e) => update("guest_name", e.target.value)} placeholder="Mr. / Mrs. Full Name" className="bg-white border-border" data-testid="guest-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Confirmation Reference</Label>
            <Input value={form.confirmation_ref} onChange={(e) => update("confirmation_ref", e.target.value)} placeholder="e.g. HTL-2026-001" className="bg-white border-border" data-testid="conf-ref" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">No. of Guests</Label>
            <Input type="number" value={form.num_guests || ""} onChange={(e) => update("num_guests", parseInt(e.target.value) || 0)} className="bg-white border-border" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
              <SelectTrigger className="bg-white border-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Hotel-specific Fields */}
      {sType === "hotel" && (
        <div className="bg-white border border-border p-5 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Hotel Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Hotel Name</Label>
              <Input value={form.hotel_name} onChange={(e) => update("hotel_name", e.target.value)} placeholder="e.g. Four Seasons Resort" className="bg-white border-border" data-testid="hotel-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Hotel Address</Label>
              <Input value={form.hotel_address} onChange={(e) => update("hotel_address", e.target.value)} placeholder="City, Country" className="bg-white border-border" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Check-in</Label>
              <Input type="date" value={form.check_in} onChange={(e) => update("check_in", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Check-out</Label>
              <Input type="date" value={form.check_out} min={form.check_in || today} onChange={(e) => update("check_out", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Nights</Label>
              <Input type="number" value={form.nights || ""} onChange={(e) => update("nights", parseInt(e.target.value) || 0)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Confirmation Date</Label>
              <Input type="date" value={form.confirmation_date} onChange={(e) => update("confirmation_date", e.target.value)} className="bg-white border-border" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Meal Plan</Label>
              <Input value={form.meal_plan} onChange={(e) => update("meal_plan", e.target.value)} placeholder="e.g. Breakfast included" className="bg-white border-border" />
            </div>
          </div>
          {/* Room Categories */}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Room Categories</Label>
              <Button variant="ghost" size="sm" onClick={addRoom} className="gap-1 text-xs h-6 text-gold hover:text-gold/80" data-testid="add-room-btn">
                <Plus size={12} /> Add Room
              </Button>
            </div>
            <div className="space-y-2">
              {(form.rooms || []).map((room, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`room-row-${idx}`}>
                  <div className="flex-1">
                    <Input value={room.room_type} onChange={(e) => updateRoom(idx, "room_type", e.target.value)} placeholder="e.g. Deluxe Suite" className="bg-white border-border text-sm h-8" data-testid={`room-type-${idx}`} />
                  </div>
                  <div className="w-20">
                    <Input type="number" value={room.num_rooms || ""} onChange={(e) => updateRoom(idx, "num_rooms", parseInt(e.target.value) || 1)} placeholder="Qty" className="bg-white border-border text-sm h-8 text-center" min={1} data-testid={`room-qty-${idx}`} />
                  </div>
                  <div className="w-28">
                    <Input type="number" value={room.rate_per_night || ""} onChange={(e) => updateRoom(idx, "rate_per_night", parseFloat(e.target.value) || 0)} placeholder="Rate/night" className="bg-white border-border text-sm h-8" data-testid={`room-rate-${idx}`} />
                  </div>
                  {form.rooms.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 shrink-0" onClick={() => removeRoom(idx)}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex text-[10px] text-muted-foreground gap-2 px-0.5">
                <span className="flex-1">Room type</span>
                <span className="w-20 text-center">Qty</span>
                <span className="w-28">Rate/night</span>
                {form.rooms.length > 1 && <span className="w-8" />}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer-specific Fields */}
      {sType === "transfer" && (
        <div className="bg-white border border-border p-5 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Transfer Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Transfer Type</Label>
              <Select value={form.transfer_type} onValueChange={(v) => update("transfer_type", v)}>
                <SelectTrigger className="bg-white border-border"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {TRANSFER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Vehicle Type</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => update("vehicle_type", v)}>
                <SelectTrigger className="bg-white border-border"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input type="date" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pickup Time</Label>
              <Input type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pickup Location</Label>
              <Input value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} placeholder="e.g. Dubai International Airport" className="bg-white border-border" data-testid="pickup-location" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Drop-off Location</Label>
              <Input value={form.dropoff_location} onChange={(e) => update("dropoff_location", e.target.value)} placeholder="e.g. Burj Al Arab Hotel" className="bg-white border-border" data-testid="dropoff-location" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Flight Number</Label>
              <Input value={form.flight_number} onChange={(e) => update("flight_number", e.target.value)} placeholder="e.g. EK-501" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">No. of Passengers</Label>
              <Input type="number" value={form.num_passengers || ""} onChange={(e) => update("num_passengers", parseInt(e.target.value) || 0)} className="bg-white border-border" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs text-muted-foreground">Driver Contact</Label>
              <Input value={form.driver_contact} onChange={(e) => update("driver_contact", e.target.value)} placeholder="Phone number or name" className="bg-white border-border" />
            </div>
          </div>
        </div>
      )}

      {/* Tour-specific Fields */}
      {sType === "tour" && (
        <div className="bg-white border border-border p-5 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Tour Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Tour Name</Label>
              <Input value={form.tour_name} onChange={(e) => update("tour_name", e.target.value)} placeholder="e.g. Dubai City Tour" className="bg-white border-border" data-testid="tour-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Tour Type</Label>
              <Select value={form.tour_type} onValueChange={(v) => update("tour_type", v)}>
                <SelectTrigger className="bg-white border-border"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {TOUR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Tour Date</Label>
              <Input type="date" value={form.tour_date} onChange={(e) => update("tour_date", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Duration</Label>
              <Input value={form.tour_duration} onChange={(e) => update("tour_duration", e.target.value)} placeholder="e.g. 6 hours, Full Day" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pickup Location</Label>
              <Input value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} placeholder="e.g. Hotel lobby" className="bg-white border-border" data-testid="pickup-location" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pickup Time</Label>
              <Input type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Drop-off Location</Label>
              <Input value={form.dropoff_location} onChange={(e) => update("dropoff_location", e.target.value)} placeholder="e.g. Hotel" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Guide Language</Label>
              <Select value={form.guide_language} onValueChange={(v) => update("guide_language", v)}>
                <SelectTrigger className="bg-white border-border"><SelectValue placeholder="Select language" /></SelectTrigger>
                <SelectContent>
                  {GUIDE_LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">No. of Guests</Label>
              <Input type="number" value={form.num_passengers || ""} onChange={(e) => update("num_passengers", parseInt(e.target.value) || 0)} className="bg-white border-border" />
            </div>
          </div>
        </div>
      )}

      {/* Meet & Greet-specific Fields */}
      {sType === "meet_and_greet" && (
        <div className="bg-white border border-border p-5 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Meet & Greet Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Airport / Location</Label>
              <Input value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} placeholder="e.g. Dubai International Airport" className="bg-white border-border" data-testid="pickup-location" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Terminal</Label>
              <Input value={form.airport_terminal} onChange={(e) => update("airport_terminal", e.target.value)} placeholder="e.g. Terminal 3" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input type="date" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Arrival Time</Label>
              <Input type="time" value={form.arrival_time} onChange={(e) => update("arrival_time", e.target.value)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Flight Number</Label>
              <Input value={form.flight_number} onChange={(e) => update("flight_number", e.target.value)} placeholder="e.g. EK-501" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Signboard Name</Label>
              <Input value={form.signboard_name} onChange={(e) => update("signboard_name", e.target.value)} placeholder="Name to display on sign" className="bg-white border-border" data-testid="signboard-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Greeter Name</Label>
              <Input value={form.greeter_name} onChange={(e) => update("greeter_name", e.target.value)} placeholder="Assigned greeter" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Transfer To</Label>
              <Input value={form.transfer_to} onChange={(e) => update("transfer_to", e.target.value)} placeholder="e.g. Four Seasons Hotel" className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Vehicle Type</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => update("vehicle_type", v)}>
                <SelectTrigger className="bg-white border-border"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Pricing & Payment */}
      <div className="bg-white border border-border p-5 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Pricing & Payment</h2>
        <div className="flex items-center gap-2 mb-3">
          <Checkbox checked={form.show_pricing} onCheckedChange={(v) => update("show_pricing", !!v)} id="show-pricing" />
          <Label htmlFor="show-pricing" className="text-xs text-muted-foreground cursor-pointer">Show pricing in PDF</Label>
        </div>
        {form.show_pricing && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sType === "hotel" ? null : (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Rate</Label>
                <Input type="number" value={form.rate_per_night || ""} onChange={(e) => update("rate_per_night", parseFloat(e.target.value) || 0)} className="bg-white border-border" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Total Cost</Label>
              <Input type="number" value={form.total_cost || ""} onChange={(e) => update("total_cost", parseFloat(e.target.value) || 0)} className="bg-white border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Payment Status</Label>
              <Input value={form.payment_status} onChange={(e) => update("payment_status", e.target.value)} placeholder="e.g. Paid, Pending" className="bg-white border-border" />
            </div>
          </div>
        )}
      </div>

      {/* Inclusions */}
      <div className="bg-white border border-border p-5 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Inclusions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {availableInclusions.map((inc) => (
            <label key={inc} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.inclusions.includes(inc)} onCheckedChange={() => toggleInclusion(inc)} />
              <span className="text-muted-foreground">{inc}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Details */}
      <div className="bg-white border border-border p-5 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Additional Details</h2>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Special Requests</Label>
            <Textarea value={form.special_requests} onChange={(e) => update("special_requests", e.target.value)} placeholder="Any special requirements" className="bg-white border-border min-h-[50px] text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Cancellation Policy</Label>
            <Textarea value={form.cancellation_policy} onChange={(e) => update("cancellation_policy", e.target.value)} placeholder="Free cancellation up to..." className="bg-white border-border min-h-[50px] text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Any additional notes" className="bg-white border-border min-h-[50px] text-xs" />
          </div>
        </div>
      </div>

      {/* Consultant / Issuer Details */}
      <div className="bg-white border border-border p-5 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-gold mb-4">Issued By (shown on PDF)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input value={form.consultant_name} onChange={(e) => update("consultant_name", e.target.value)} placeholder="e.g. Pratyush Sareen" className="bg-white border-border" data-testid="consultant-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Designation</Label>
            <Input value={form.consultant_designation} onChange={(e) => update("consultant_designation", e.target.value)} placeholder="e.g. Travel Consultant" className="bg-white border-border" data-testid="consultant-designation" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Phone</Label>
            <Input value={form.consultant_phone} onChange={(e) => update("consultant_phone", e.target.value)} placeholder="+91 XXXXX XXXXX" className="bg-white border-border" data-testid="consultant-phone" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input value={form.consultant_email} onChange={(e) => update("consultant_email", e.target.value)} placeholder="email@company.com" className="bg-white border-border" data-testid="consultant-email" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationEditorPage;
